import { ChangeDetectionStrategy, Component, ElementRef, input, OnDestroy, OnInit, viewChild } from '@angular/core'
import { isServer } from '@mos/client/shared/utils'
import { EmitterConfig } from '../models/emitter.config'
import { Particle } from '../models/particle'

@Component({
	selector: 'mos-canvas-emitter',
	imports: [],
	templateUrl: './canvas-emitter.component.html',
	styleUrls: ['./canvas-emitter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CanvasEmitterComponent implements OnInit, OnDestroy {
	readonly config = input<EmitterConfig>({
		displayArea: { width: 560, height: 100 },
	})
	readonly canvas = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas')
	private ctx!: CanvasRenderingContext2D
	private particles: Particle[] = []
	private intervalId: number | null = null
	private animationFrameId: number | null = null
	private lastFrameTime: number = 0
	private readonly frameDuration: number = 1000 / 15 // 15fps

	ngOnInit() {
		if (isServer) {
			return
		}

		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		this.ctx = this.canvas().nativeElement.getContext('2d', { willReadFrequently: true })!
		this.setCanvasSize()
		this.startEmitter()
		this.animate(0)
	}

	ngOnDestroy() {
		if (isServer) {
			return
		}

		if (this.intervalId !== null) {
			clearInterval(this.intervalId)
		}
		if (this.animationFrameId !== null) {
			// cancelAnimationFrame peut ne pas exister côté serveur, mais on est sûr d'être en browser
			cancelAnimationFrame(this.animationFrameId)
		}
	}

	private setCanvasSize() {
		this.canvas().nativeElement.width = this.config().displayArea.width
		this.canvas().nativeElement.height = this.config().displayArea.height
	}

	private startEmitter() {
		const spawnRate = this.config().spawnRate ?? 1
		if (spawnRate === 0) {
			return
		}
		this.intervalId = window.setInterval(() => {
			this.spawnParticle(this.config())
		}, 1000 / spawnRate)
	}

	private spawnParticle(config: EmitterConfig) {
		this.particles.push(new Particle(config))
	}

	public burst(quantity: number, config: EmitterConfig = this.config()) {
		for (let i = 0; i < quantity; i++) {
			this.spawnParticle(config)
		}
	}

	private animate = (timestamp: number) => {
		const elapsed = timestamp - this.lastFrameTime
		if (elapsed > this.frameDuration) {
			this.lastFrameTime = timestamp
			this.ctx.clearRect(0, 0, this.canvas().nativeElement.width, this.canvas().nativeElement.height)
			this.particles.forEach((particle) => {
				particle.update(elapsed)
				particle.draw(this.ctx)
			})
			// Remove particles in a separate loop to avoid flickering
			this.particles.forEach((particle, index) => {
				if (particle.isExpired()) {
					this.particles.splice(index, 1)
				}
			})
		}
		this.animationFrameId = requestAnimationFrame(this.animate)
	}
}
